import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { getCached, setCached, clearCached, STATIC_LOOKUP_CACHE_TTL_MS } from '../../utils/staticLookupCache';
import { updateError } from './user';
import { dispatch } from '../store';

const initialState = {
    tablePreferenceLoading: false,
    tablePreferences: [],
    tablePreferencesOrigin: null,
    globalTablePreferences: [],
    practiceTablePreferences: { results: [] }
};

const slice = createSlice({
    name: 'tablePreferences',
    initialState,
    reducers: {
        resetTablePreferences(state) {
            Object.assign(state, initialState);
        },
        clearTablePreferences(state) {
            state.tablePreferences = [];
            state.tablePreferencesOrigin = null;
        },
        setTablePreferenceLoading(state, action) {
            state.tablePreferenceLoading = action.payload;
        },
        setTablePreferences(state, action) {
            state.tablePreferences = action.payload;
            // Provenance belongs to this publication only; ordinary refreshes
            // and external updates must never inherit an earlier save's origin.
            state.tablePreferencesOrigin = action.meta?.preferenceOrigin ?? null;
        },
        setGlobalTablePreferences(state, action) {
            state.globalTablePreferences = action.payload;
        },
        upsertGlobalTablePreference(state, action) {
            // Insert or replace a single global pref by id (preserves the rest of
            // the array, so a successful save doesn't blank the editor list).
            const next = action.payload;
            if (!next) return;
            const list = state.globalTablePreferences?.results
                ? state.globalTablePreferences.results
                : state.globalTablePreferences || [];
            const idx = list.findIndex((r) => r?.id === next.id);
            if (idx >= 0) {
                list[idx] = next;
            } else {
                list.push(next);
            }
        },
        removeGlobalTablePreference(state, action) {
            const removeId = action.payload;
            const list = state.globalTablePreferences?.results
                ? state.globalTablePreferences.results
                : state.globalTablePreferences || [];
            const idx = list.findIndex((r) => r?.id === removeId);
            if (idx >= 0) list.splice(idx, 1);
        },
        setPracticeTablePreferences(state, action) {
            state.practiceTablePreferences.results = action.payload;
        }
    }
});

export default slice.reducer;

export const { setTablePreferences, resetTablePreferences, clearTablePreferences, setTablePreferenceLoading, setGlobalTablePreferences, setPracticeTablePreferences, upsertGlobalTablePreference, removeGlobalTablePreference } = slice.actions;

const handleStopLoading = (dispatchPreference = dispatch) => {
    setTimeout(() => {
        dispatchPreference(slice.actions.setTablePreferenceLoading(false));
    }, 500);
};

export const getAuthoritativeTablePreferencePracticeId = (getState) => {
    const practiceId = getState?.()?.practiceTreatmentList?.practiceId;
    return practiceId === null || practiceId === undefined || practiceId === '' ? null : practiceId;
};

const isCurrentTablePreferencePractice = (getState, practiceId) =>
    String(getAuthoritativeTablePreferencePracticeId(getState)) === String(practiceId);

// The optional hook lifetime check stays inside the thunk closure. A save may
// finish after its grid was disposed, but its refresh must not publish into a
// newly mounted grid, even when practice/user/table identifiers match again.
const canPublishTablePreferences = (getState, practiceId, data) =>
    isCurrentTablePreferencePractice(getState, practiceId) && (!data?.isCurrent || data.isCurrent());

const requireTablePreferencePractice = (reduxDispatch, getState) => {
    const practiceId = getAuthoritativeTablePreferencePracticeId(getState);
    if (practiceId === null) {
        reduxDispatch(updateError({
            success: false,
            message: 'Select a practice before saving table preferences.',
        }));
    }
    return practiceId;
};

const authoritativeTablePreferenceBody = (body, practiceId) => ({
    ...(body || {}),
    practice: practiceId,
});

export function getTablePreferences(data) {
    return async (reduxDispatch, getState) => {
        const practiceId = requireTablePreferencePractice(reduxDispatch, getState);
        if (practiceId === null) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/users/${data?.practiceUserId}/table-column-preferences/?fields={id,table_name,preferences}`);
            if (!canPublishTablePreferences(getState, practiceId, data)) return null;
            handleStopLoading(reduxDispatch);
            if (response.status === 200) {
                const publication = slice.actions.setTablePreferences(response.data);
                reduxDispatch(data?.refreshOrigin ? {
                    ...publication,
                    meta: { preferenceOrigin: data.refreshOrigin },
                } : publication);
            } else {
                reduxDispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }
            return response;
        } catch (error) {
            if (canPublishTablePreferences(getState, practiceId, data)) {
                handleStopLoading(reduxDispatch);
                reduxDispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            }
            return null;
        }
    };
}

// System-wide baseline (not practice- or user-scoped — see the write-thunk
// note below), so the cache key is a fixed global name.
const GLOBAL_TABLE_PREFERENCES_CACHE_KEY = 'globaltablecolumnpreference';

export function getGlobalTablePreferences() {
    return async () => {
        try {
            const cached = getCached(GLOBAL_TABLE_PREFERENCES_CACHE_KEY, { ttlMs: STATIC_LOOKUP_CACHE_TTL_MS });
            if (cached !== undefined) {
                dispatch(slice.actions.setGlobalTablePreferences(cached));
                return;
            }
            const response = await callApi('/api/v1/utils/globaltablecolumnpreference/');
            handleStopLoading();
            if (response.status === 200) {
                dispatch(slice.actions.setGlobalTablePreferences(response.data));
                setCached(GLOBAL_TABLE_PREFERENCES_CACHE_KEY, response.data);
            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getPracticeTablePreferences(practiceId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/table-column-preferences/`);
            if (response.status === 200) {
                dispatch(slice.actions.setPracticeTablePreferences(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to load practice table preferences' }));
        }
    };
}

export function handleTablePreferences(data) {
    return async (reduxDispatch, getState) => {
        const practiceId = requireTablePreferencePractice(reduxDispatch, getState);
        if (practiceId === null) return null;
        try {
            if (!data?.body?.table_name) {
                handleStopLoading(reduxDispatch);
                return null;
            }
            const response = await callApi(
                `/api/v1/practices/${practiceId}/users/${data?.practiceUserId}/table-column-preferences/`,
                'post',
                authoritativeTablePreferenceBody(data.body, practiceId)
            );
            if (!canPublishTablePreferences(getState, practiceId, data)) return null;
            if (response.status === 201) {
                reduxDispatch(getTablePreferences({ practiceUserId: data?.practiceUserId, ...(data?.refreshOrigin ? { refreshOrigin: data.refreshOrigin } : {}), ...(data?.isCurrent ? { isCurrent: data.isCurrent } : {}) }));
                reduxDispatch(updateError({ success: true, message: 'Column visibility stored successfully' }));
            } else {
                handleStopLoading(reduxDispatch);
                reduxDispatch(updateError({ success: false, message: response?.data?.detail || 'Something went wrong' }));
            }
            return response;
        } catch (error) {
            if (canPublishTablePreferences(getState, practiceId, data)) {
                handleStopLoading(reduxDispatch);
                reduxDispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            }
            return null;
        }
    };
}

export function handleTablePreferencesEdit(data) {
    return async (reduxDispatch, getState) => {
        const practiceId = requireTablePreferencePractice(reduxDispatch, getState);
        if (practiceId === null) return null;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/users/${data?.practiceUserId}/table-column-preferences/${data?.tableId}`,
                'patch',
                authoritativeTablePreferenceBody(data.body, practiceId)
            );
            if (!canPublishTablePreferences(getState, practiceId, data)) return null;
            if (response.status === 200) {
                reduxDispatch(getTablePreferences({ practiceUserId: data?.practiceUserId, ...(data?.refreshOrigin ? { refreshOrigin: data.refreshOrigin } : {}), ...(data?.isCurrent ? { isCurrent: data.isCurrent } : {}) }));
                reduxDispatch(updateError({ success: true, message: 'Table preference updated successfully' }));
            } else {
                handleStopLoading(reduxDispatch);
                reduxDispatch(updateError({ success: false, message: JSON.stringify(response.data?.columns) || 'Something went wrong' }));
            }
            return response;
        } catch (error) {
            if (canPublishTablePreferences(getState, practiceId, data)) {
                handleStopLoading(reduxDispatch);
                reduxDispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            }
            return null;
        }
    };
}

// ── System-default (global) write thunks ──────────────────────────────────────
// These target `/api/v1/utils/globaltablecolumnpreference/` — the system-wide
// baseline that every practice falls through to when it has no record of its
// own (see resolution cascade in src/utils/tablePreferences.js). Editing here
// affects every practice that hasn't customised the table.
//
// Body shape mirrors the per-practice endpoint minus `practice` and `target_users`:
//   { table_name, preferences: { columns, isCombinedName, preferences } }

// Extract a user-presentable error message from a non-OK response. The
// per-practice TableColumnPreference serializer returns top-level `columns`
// validation errors; the global serializer does NOT have a top-level `columns`
// field (columns live nested inside `preferences.preferences.columns`), so the
// per-practice extraction prints "undefined" here. Prefer `detail`, then any
// serialized non-field errors, then a generic fallback.
const extractGlobalSaveError = (response, fallback) => {
    const d = response?.data;
    if (typeof d === 'string') return d;
    if (d?.detail) return d.detail;
    if (Array.isArray(d?.non_field_errors) && d.non_field_errors.length) return d.non_field_errors.join('; ');
    if (d && typeof d === 'object' && Object.keys(d).length) {
        try { return JSON.stringify(d); } catch (_) { /* fall through */ }
    }
    return fallback;
};

export function handleGlobalTablePreferences(data) {
    return async () => {
        try {
            if (!data?.body?.table_name) {
                handleStopLoading();
                return null;
            }
            const response = await callApi('/api/v1/utils/globaltablecolumnpreference/', 'post', data.body);
            if (response.status === 201 || response.status === 200) {
                dispatch(slice.actions.upsertGlobalTablePreference(response.data));
                // Invalidate the sessionStorage TTL cache (getGlobalTablePreferences)
                // so a reload within the 10-min TTL doesn't serve pre-write data.
                // Clearing (rather than patching the cached array in place) is
                // simplest and always correct.
                clearCached(GLOBAL_TABLE_PREFERENCES_CACHE_KEY);
                // Caller (handleSystemDefaultSave / handleResetSystemDefault) shows
                // its own snackbar — emitting success here too would double-toast.
                return response.data;
            }
            handleStopLoading();
            dispatch(updateError({ success: false, message: extractGlobalSaveError(response, 'Failed to save system default') }));
            return null;
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Failed to save system default' }));
            return null;
        }
    };
}

export function handleGlobalTablePreferencesEdit(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/utils/globaltablecolumnpreference/${data?.tableId}/`, 'patch', data.body);
            if (response.status === 200) {
                dispatch(slice.actions.upsertGlobalTablePreference(response.data));
                // See handleGlobalTablePreferences — same TTL-cache invalidation.
                clearCached(GLOBAL_TABLE_PREFERENCES_CACHE_KEY);
                // No success snackbar here — caller handles user-visible success.
                return response.data;
            }
            handleStopLoading();
            dispatch(updateError({ success: false, message: extractGlobalSaveError(response, 'Failed to update system default') }));
            return null;
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Failed to update system default' }));
            return null;
        }
    };
}

export function handleGlobalTablePreferencesDelete(data) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/utils/globaltablecolumnpreference/${data?.tableId}/`,
                'delete',
            );
            if (response.status >= 400) {
                dispatch(slice.actions.setTablePreferenceLoading(false));
                dispatch(updateError({ success: false, message: 'Failed to reset system default — please try again' }));
                return false;
            }
            dispatch(slice.actions.removeGlobalTablePreference(data?.tableId));
            dispatch(slice.actions.setTablePreferenceLoading(false));
            // See handleGlobalTablePreferences — same TTL-cache invalidation.
            clearCached(GLOBAL_TABLE_PREFERENCES_CACHE_KEY);
            return true;
        } catch (error) {
            dispatch(slice.actions.setTablePreferenceLoading(false));
            dispatch(updateError({ success: false, message: 'Failed to reset system default — please try again' }));
            return false;
        }
    };
}

export function handleTablePreferencesDelete(data) {
    return async (reduxDispatch, getState) => {
        const practiceId = requireTablePreferencePractice(reduxDispatch, getState);
        if (practiceId === null) return false;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/users/${data?.practiceUserId}/table-column-preferences/${data?.tableId}/`,
                'delete'
            );
            if (!isCurrentTablePreferencePractice(getState, practiceId)) return false;
            if (response.status >= 400) {
                reduxDispatch(slice.actions.setTablePreferenceLoading(false));
                reduxDispatch(updateError({ success: false, message: 'Failed to reset column preferences — please try again' }));
                return false;
            }
            // Re-fetch to reflect the deletion; on re-fetch failure, surface an error rather than silently stalling.
            try {
                await reduxDispatch(getTablePreferences({ practiceUserId: data?.practiceUserId }));
            } catch (refetchError) {
                reduxDispatch(updateError({ success: false, message: 'Column preferences reset but could not reload — please refresh' }));
            }
            reduxDispatch(slice.actions.setTablePreferenceLoading(false));
            return true;
        } catch (error) {
            if (isCurrentTablePreferencePractice(getState, practiceId)) {
                reduxDispatch(slice.actions.setTablePreferenceLoading(false));
                reduxDispatch(updateError({ success: false, message: 'Failed to reset column preferences — please try again' }));
            }
            return false;
        }
    };
}
